import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Plane, Dumbbell, ChevronRight, PartyPopper, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useRealtime } from '../context/RealtimeContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { AvatarStack } from '../components/ui/avatar-initials';
import EmptyState from '../components/EmptyState';
import { EmptyGroups } from '../components/illustrations';
import useDraftAutoSave from '../hooks/useDraftAutoSave';
import GroupEmojiPicker from '../components/GroupEmojiPicker';
import PendingInvitesDialog from '../components/PendingInvitesDialog';
import { formatNXC } from '../lib/formatNXC';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const groupTypeIcons = {
  sports: Dumbbell,
  trip: Plane,
  event: PartyPopper,
  daily: Home,
  custom: Home, // legacy alias — old groups map to "daily" UX
};

// iter224 — sport emoji tile map. Falls back to the group_type icon when
// a sports-type group has no sport set yet.
const sportEmoji = {
  pickleball: '🥒',
  paddle: '🎾',
  badminton: '🏸',
  lawn_tennis: '🎾',
  cricket: '🏏',
  football: '⚽',
  volleyball: '🏐',
  table_tennis: '🏓',
  basketball: '🏀',
  other: '🏆',
};

const sportTileTone = {
  pickleball: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  paddle: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30',
  badminton: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
  lawn_tennis: 'bg-lime-500/15 text-lime-700 dark:text-lime-300 border-lime-500/30',
  cricket: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  football: 'bg-neutral-500/15 text-neutral-800 dark:text-neutral-200 border-neutral-500/30',
  volleyball: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  table_tennis: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30',
  basketball: 'bg-amber-600/15 text-amber-700 dark:text-amber-300 border-amber-600/30',
  other: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
};

const groupTypeColors = {
  sports: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
  trip: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
  event: 'bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30',
  daily: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
  custom: 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30',
};

const groupTypeLabels = {
  sports: 'Sports',
  trip: 'Trip',
  event: 'Event',
  daily: 'Daily / Household',
  custom: 'Daily / Household',
};

const currencies = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

const getCurrencySymbol = (code) => {
  const currency = currencies.find(c => c.code === code);
  return currency?.symbol || '₹';
};

export default function GroupsPage() {
  const navigate = useNavigate();
  const { onEvent } = useRealtime();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    group_type: 'sports',
    member_phones: '',
    currency: 'INR',
    event_date: '',
    target_budget: '',
    trip_start_date: '',
    trip_end_date: '',
  });
  const [creating, setCreating] = useState(false);
  const [postCreateInvites, setPostCreateInvites] = useState(null); // iter247 — {group, phones}
  const [existingUsers, setExistingUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Durability: persist Create Group draft until submitted
  const { clearDraft: clearGroupDraft } = useDraftAutoSave({
    formKey: 'createGroup',
    value: newGroup,
    onRestore: (saved) => setNewGroup((prev) => ({ ...prev, ...saved })),
    enabled: showCreateModal,
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  // Live refresh: any event that could change a group's balance / member count
  useEffect(() => {
    return onEvent(
      [
        'expense_pending',
        'expense_approved',
        'expense_rejected',
        'expense_reversed',
        'expense_deleted',
        'coin_transfer_approved',
        'coins_allotted',
        'fine_issued',
        'fine_reversed',
        'recurring_expense',
        'pledge_received',
        'member_joined',
        'member_removed',
        'group_admin_promoted',
        'group_admin_demoted',
      ],
      () => { fetchGroups(); }
    );
  }, [onEvent]);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingUsers = async () => {
    try {
      const res = await api.get('/users/search?name=');
      // iter247 — dedupe by id (duplicate rows caused React duplicate-key spam)
      const seen = new Set();
      const unique = (res.data || []).filter((u) => {
        if (!u?.id || seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
      setExistingUsers(unique);
    } catch { }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setSelectedMembers([]);
    setUserSearch('');
    fetchExistingUsers();
  };

  const toggleMember = (user) => {
    if (selectedMembers.find(m => m.id === user.id)) {
      setSelectedMembers(selectedMembers.filter(m => m.id !== user.id));
    } else {
      setSelectedMembers([...selectedMembers, user]);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setCreating(true);
    try {
      // Combine selected members' phones + manually typed phones
      const selectedPhones = selectedMembers.map(m => m.phone);
      const manualPhones = newGroup.member_phones
        .split(',')
        .map(p => p.trim())
        .filter(p => p);
      const allPhones = [...new Set([...selectedPhones, ...manualPhones])];

      const payload = {
        name: newGroup.name,
        group_type: newGroup.group_type,
        member_phones: allPhones,
        currency: newGroup.currency,
      };
      if (newGroup.emoji) payload.emoji = newGroup.emoji;
      if (newGroup.group_type === 'event') {
        if (newGroup.event_date) payload.event_date = newGroup.event_date;
        if (newGroup.target_budget) payload.target_budget = parseFloat(newGroup.target_budget);
      } else if (newGroup.group_type === 'trip') {
        if (newGroup.trip_start_date) payload.trip_start_date = newGroup.trip_start_date;
        if (newGroup.trip_end_date) payload.trip_end_date = newGroup.trip_end_date;
      }

      const response = await api.post('/groups', payload);

      setGroups([response.data, ...groups]);
      setShowCreateModal(false);
      setNewGroup({
        name: '', group_type: 'sports', member_phones: '', currency: 'INR', emoji: '',
        event_date: '', target_budget: '', trip_start_date: '', trip_end_date: '',
      });
      setSelectedMembers([]);
      clearGroupDraft();
      toast.success('Group created!');
      // iter247 — surface phones with no account instead of dropping them silently
      const notFound = response.data?.not_found_phones || [];
      if (notFound.length > 0) {
        setPostCreateInvites({ group: response.data, phones: notFound });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount /* , _currencyCode */) => formatNXC(amount);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="groups-loading">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="groups-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold">Groups</h1>
        <Button
          onClick={handleOpenCreateModal}
          data-testid="create-group-btn"
          className="rounded-full"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Group
        </Button>
      </div>

      {/* Groups List */}
      <div className="space-y-3">
        {groups.length === 0 ? (
          <EmptyState
            illustration={EmptyGroups}
            title="No groups yet"
            description="Create a group to start tracking expenses with friends, teammates, or roommates."
            size="lg"
            testId="no-groups-empty"
            cta={
              <Button onClick={handleOpenCreateModal} className="rounded-full press-scale">
                <Plus className="w-5 h-5 mr-2" />
                Create Group
              </Button>
            }
          />
        ) : (
          groups.map((group, index) => {
            const Icon = groupTypeIcons[group.group_type] || Home;
            const currency = group.currency || 'INR';
            // iter245 — custom group emoji wins; else sport emoji for sports groups
            const emoji = group.emoji || (group.group_type === 'sports' ? sportEmoji[group.sport] : null);
            const sportToneClass = group.group_type === 'sports' && group.sport
              ? (sportTileTone[group.sport] || sportTileTone.other)
              : null;

            return (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Card
                  className="p-4 border-2 rounded-2xl cursor-pointer hover:border-primary/40 hover:shadow-md transition-all expense-item"
                  onClick={() => navigate(`/groups/${group.id}`)}
                  data-testid={`group-item-${group.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${
                        sportToneClass || groupTypeColors[group.group_type]
                      }`}
                      data-testid={emoji ? `group-sport-tile-${group.sport}` : `group-type-tile-${group.group_type}`}
                    >
                      {emoji ? (
                        <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-bold truncate">{group.name}</h3>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <AvatarStack users={(group.members || []).slice(0, 4)} max={4} size="xs" />
                        <span className="text-xs text-muted-foreground">
                          {group.members?.length || 0} members
                        </span>
                        {group.group_type === 'sports' && group.sport && (
                          <span
                            className="inline-flex items-center gap-0.5 text-xs font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
                            data-testid={`group-sport-chip-${group.sport}`}
                          >
                            <span aria-hidden="true">{sportEmoji[group.sport] || '🏆'}</span>
                            <span>{(group.sport || '').replace('_', ' ')}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        {group.user_balance?.to_pay > 0 && (
                          <p className="text-sm font-bold font-display text-negative">
                            −{formatCurrency(group.user_balance.to_pay, currency)}
                          </p>
                        )}
                        {group.user_balance?.to_receive > 0 && (
                          <p className="text-sm font-bold font-display text-positive">
                            +{formatCurrency(group.user_balance.to_receive, currency)}
                          </p>
                        )}
                        {(!group.user_balance?.to_pay && !group.user_balance?.to_receive) && (
                          <span title="No dues between anyone in this group" className="inline-block text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-bold uppercase tracking-wider">All settled ✓</span>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Create Group Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md" data-testid="create-group-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create New Group</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateGroup} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">
                Group Name
              </Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="e.g., Badminton Squad"
                data-testid="group-name-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>

            {/* iter245 — optional group emoji */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">
                Emoji (Optional)
              </Label>
              <GroupEmojiPicker
                value={newGroup.emoji}
                onChange={(e) => setNewGroup({ ...newGroup, emoji: e })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  Group Type
                </Label>
                <Select
                  value={newGroup.group_type}
                  onValueChange={(value) => setNewGroup({ ...newGroup, group_type: value })}
                >
                  <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="group-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sports">
                      <div className="flex items-center gap-2">
                        <Dumbbell className="w-4 h-4" />
                        Sports
                      </div>
                    </SelectItem>
                    <SelectItem value="trip">
                      <div className="flex items-center gap-2">
                        <Plane className="w-4 h-4" />
                        Trip
                      </div>
                    </SelectItem>
                    <SelectItem value="event">
                      <div className="flex items-center gap-2">
                        <PartyPopper className="w-4 h-4" />
                        Event
                      </div>
                    </SelectItem>
                    <SelectItem value="daily">
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        Daily / Household
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  Currency
                </Label>
                <Select
                  value={newGroup.currency}
                  onValueChange={(value) => setNewGroup({ ...newGroup, currency: value })}
                >
                  <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="group-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{curr.symbol}</span>
                          {curr.code}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type-specific optional fields */}
            {newGroup.group_type === 'event' && (
              <div className="grid grid-cols-2 gap-4" data-testid="event-fields">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Event Date</Label>
                  <Input
                    type="date"
                    value={newGroup.event_date}
                    onChange={(e) => setNewGroup({ ...newGroup, event_date: e.target.value })}
                    data-testid="event-date-input"
                    className="h-12 border-2 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Target Budget</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 25000"
                    value={newGroup.target_budget}
                    onChange={(e) => setNewGroup({ ...newGroup, target_budget: e.target.value })}
                    data-testid="event-budget-input"
                    className="h-12 border-2 rounded-xl"
                  />
                </div>
              </div>
            )}

            {newGroup.group_type === 'trip' && (
              <div className="grid grid-cols-2 gap-4" data-testid="trip-fields">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Start Date</Label>
                  <Input
                    type="date"
                    value={newGroup.trip_start_date}
                    onChange={(e) => setNewGroup({ ...newGroup, trip_start_date: e.target.value })}
                    data-testid="trip-start-input"
                    className="h-12 border-2 rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold uppercase tracking-wide">End Date</Label>
                  <Input
                    type="date"
                    value={newGroup.trip_end_date}
                    onChange={(e) => setNewGroup({ ...newGroup, trip_end_date: e.target.value })}
                    data-testid="trip-end-input"
                    className="h-12 border-2 rounded-xl"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">
                Add Members (Optional)
              </Label>

              {/* Selected members chips */}
              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedMembers.map(m => (
                    <span key={m.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      {m.name}
                      <button type="button" onClick={() => toggleMember(m)} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Existing users picker */}
              {existingUsers.length > 0 && (
                <div className="border-2 rounded-xl p-2 max-h-32 overflow-y-auto space-y-1">
                  {existingUsers
                    .filter(u => u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.phone?.includes(userSearch))
                    .map(u => (
                      <button
                        type="button"
                        key={u.id}
                        onClick={() => toggleMember(u)}
                        data-testid={`select-member-${u.id}`}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm flex items-center justify-between transition-colors ${
                          selectedMembers.find(m => m.id === u.id) ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
                        }`}
                      >
                        <span>{u.name} <span className="text-muted-foreground text-xs">({u.phone})</span></span>
                        {selectedMembers.find(m => m.id === u.id) && <span className="text-primary font-bold">✓</span>}
                      </button>
                    ))}
                </div>
              )}

              {/* Manual phone input */}
              <Input
                value={newGroup.member_phones}
                onChange={(e) => setNewGroup({ ...newGroup, member_phones: e.target.value })}
                placeholder="Or type phone numbers: 9820030046, 9820690190"
                data-testid="member-phones-input"
                className="h-12 border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">
                Select above or type phone numbers separated by commas
              </p>
            </div>

            <Button
              type="submit"
              disabled={creating}
              data-testid="submit-create-group-btn"
              className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
            >
              {creating ? 'Creating...' : 'Create Group'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter247 — post-create "these numbers aren't on SplitEasy" follow-up */}
      <PendingInvitesDialog
        open={!!postCreateInvites}
        onClose={() => setPostCreateInvites(null)}
        group={postCreateInvites?.group}
        phones={postCreateInvites?.phones || []}
      />
    </div>
  );
}
